import {
  COMFORT_VK_DEDUCTIBLE,
  FALLBACK_TK_DEDUCTIBLE,
  FALLBACK_VK_DEDUCTIBLE,
  INSURANCE_UPGRADE_GROUP_ID,
  OLD_DRIVER_ADDON_ID,
  YOUNG_DRIVER_ADDON_ID,
} from '@finn/ua-constants';

import { Deal } from '../hooks/useDeal';
import { AddonsResponse } from '../hooks/useGetAddons';
import { Addon } from '../types/addons';

export const PREMIUM_DEDUCTIBLE_VALUE = 500;
export const PREMIUM_DEDUCTIBLE_VALUE_IN_CENTS = 50000;

export const hasYoungOrOldDriverInAddons = (addonsResponse: AddonsResponse) => {
  return (
    addonsResponse?.appliedAddonIds.includes(YOUNG_DRIVER_ADDON_ID) ||
    addonsResponse?.appliedAddonIds.includes(OLD_DRIVER_ADDON_ID)
  );
};

export const getInsuranceAddons = (addonsResponse: AddonsResponse) => {
  const insuranceAddonGroup = addonsResponse?.allAddons?.groups.find(
    (group) => group.group.group_id === INSURANCE_UPGRADE_GROUP_ID
  );

  return insuranceAddonGroup?.addons;
};

export const getAppliedInsuranceAddon = (addonsResponse: AddonsResponse) => {
  const insuranceAddonGroup = addonsResponse?.appliedAddons?.groups.find(
    (group) => group.group.group_id === INSURANCE_UPGRADE_GROUP_ID
  );

  return insuranceAddonGroup?.addons?.[0];
};

export const getAddonTitle = (addon?: Addon) => {
  if (!addon) return '';

  return addon?.display_info?.title;
};

export const canUpgradeInsuranceWithAddons = (
  addonsResponse: AddonsResponse
) => {
  if (!addonsResponse) return false;
  const hasGroupId = addonsResponse?.availableGroupIds.includes(
    INSURANCE_UPGRADE_GROUP_ID
  );

  const insuranceAddons = getInsuranceAddons(addonsResponse);

  const mostExpensiveInsuranceObject = insuranceAddons?.reduce(
    (max, addon) =>
      addon.pricing.amount.gross > max.pricing.amount.gross ? addon : max,
    insuranceAddons?.[0]
  );

  const hasMostExpensiveInsurance = addonsResponse?.appliedAddonIds.includes(
    mostExpensiveInsuranceObject?.addon_id
  );

  return hasGroupId || (!hasGroupId && !hasMostExpensiveInsurance);
};

export const hasPremiumInsuranceInDeal = (deal: Deal) => {
  return (
    Number(deal?.damage_deductible_vollkasko) ===
      PREMIUM_DEDUCTIBLE_VALUE_IN_CENTS ||
    Number(deal?.customer_specific_vk_damage_deductible) ===
      PREMIUM_DEDUCTIBLE_VALUE
  );
};

export const getVkAndTkValues = (
  addonsResponse: AddonsResponse,
  deal: Deal
) => {
  const appliedInsuranceAddon = getAppliedInsuranceAddon(addonsResponse);
  const isPremiumInDeal = hasPremiumInsuranceInDeal(deal);

  const appliedInsuranceVkValue =
    appliedInsuranceAddon?.attributes?.coverage?.full;
  const appliedInsuranceTkValue =
    appliedInsuranceAddon?.attributes?.coverage?.partial;

  if (appliedInsuranceVkValue && appliedInsuranceTkValue) {
    return {
      vkValue: appliedInsuranceVkValue / 100,
      tkValue: appliedInsuranceTkValue / 100,
    };
  }

  if (isPremiumInDeal) {
    return {
      vkValue: COMFORT_VK_DEDUCTIBLE,
      tkValue: FALLBACK_TK_DEDUCTIBLE,
    };
  }

  return {
    vkValue: FALLBACK_VK_DEDUCTIBLE,
    tkValue: FALLBACK_TK_DEDUCTIBLE,
  };
};
